/** Leitura mínima de DER (ASN.1), só o que é preciso para ler uma
 *  assinatura CMS/CAdES de um PDF ou de um contentor ASiC: quem assinou
 *  (o nome do certificado), quando, com que algoritmo de resumo e qual
 *  o resumo que ficou assinado.
 *
 *  Não verifica a assinatura criptográfica nem a cadeia de confiança do
 *  certificado: isso é trabalho do Autenticação.gov ou do validador da
 *  Comissão Europeia, e a interface di-lo. Puro, sem dependências. */

export interface NoDer {
  /** Etiqueta completa (primeiro byte: classe, construído, número). */
  etiqueta: number
  /** Início do conteúdo e fim (exclusivo), em `bytes`. */
  inicio: number
  fim: number
  filhos: NoDer[]
}

const CONSTRUIDO = 0x20

/** Lê um nó DER a partir de `pos`. Atira se os comprimentos não batem. */
export function lerNo(bytes: Uint8Array, pos = 0): NoDer & { seguinte: number } {
  if (pos + 2 > bytes.length) throw new Error('DER truncado')
  const etiqueta = bytes[pos]
  if ((etiqueta & 0x1f) === 0x1f) throw new Error('etiqueta DER longa não suportada')
  let comp = bytes[pos + 1]
  let p = pos + 2
  if (comp & 0x80) {
    const n = comp & 0x7f
    if (n === 0 || n > 4) throw new Error('comprimento DER indefinido ou demasiado grande')
    comp = 0
    for (let i = 0; i < n; i++) comp = comp * 256 + bytes[p + i]
    p += n
  }
  const fim = p + comp
  if (fim > bytes.length) throw new Error('DER truncado')
  const filhos: NoDer[] = []
  if (etiqueta & CONSTRUIDO) {
    let q = p
    while (q < fim) {
      const f = lerNo(bytes, q)
      filhos.push(f)
      q = f.seguinte
    }
  }
  return { etiqueta, inicio: p, fim, filhos, seguinte: fim }
}

export function conteudo(bytes: Uint8Array, n: NoDer): Uint8Array {
  return bytes.subarray(n.inicio, n.fim)
}

/** OID em texto ("1.2.840.113549.1.9.4"). */
export function oid(bytes: Uint8Array, n: NoDer): string {
  const c = conteudo(bytes, n)
  if (c.length === 0) return ''
  const partes = [Math.floor(c[0] / 40), c[0] % 40]
  let v = 0
  for (let i = 1; i < c.length; i++) {
    v = v * 128 + (c[i] & 0x7f)
    if (!(c[i] & 0x80)) {
      partes.push(v)
      v = 0
    }
  }
  return partes.join('.')
}

/** Texto de uma string DER (UTF8String, PrintableString, IA5, BMP, T61). */
export function texto(bytes: Uint8Array, n: NoDer): string {
  const c = conteudo(bytes, n)
  if (n.etiqueta === 0x1e) {
    // BMPString: UTF-16 big-endian.
    let s = ''
    for (let i = 0; i + 1 < c.length; i += 2) s += String.fromCharCode((c[i] << 8) | c[i + 1])
    return s
  }
  if (n.etiqueta === 0x0c) return new TextDecoder('utf-8').decode(c)
  // PrintableString, IA5String, T61String: tratados como latin1.
  let s = ''
  for (const b of c) s += String.fromCharCode(b)
  return s
}

/** Data de um UTCTime (0x17) ou GeneralizedTime (0x18), em ISO. */
export function data(bytes: Uint8Array, n: NoDer): string | null {
  const t = texto(bytes, n)
  const m = n.etiqueta === 0x17 ? /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?Z$/.exec(t) : /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?(?:\.\d+)?Z$/.exec(t)
  if (!m) return null
  let ano = Number(m[1])
  if (n.etiqueta === 0x17) ano += ano < 50 ? 2000 : 1900
  return `${String(ano).padStart(4, '0')}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? '00'}Z`
}

export function hex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
}

const OID_SIGNED_DATA = '1.2.840.113549.1.7.2'
const OID_MESSAGE_DIGEST = '1.2.840.113549.1.9.4'
const OID_SIGNING_TIME = '1.2.840.113549.1.9.5'
const OID_CN = '2.5.4.3'

export const RESUMOS: Record<string, 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512'> = {
  '1.3.14.3.2.26': 'SHA-1',
  '2.16.840.1.101.3.4.2.1': 'SHA-256',
  '2.16.840.1.101.3.4.2.2': 'SHA-384',
  '2.16.840.1.101.3.4.2.3': 'SHA-512',
}

/** O nome comum (CN) de um Name X.501. */
function nomeComum(bytes: Uint8Array, nome: NoDer): string | null {
  for (const rdn of nome.filhos) {
    for (const atv of rdn.filhos) {
      const [t, v] = atv.filhos
      if (t?.etiqueta === 0x06 && v && oid(bytes, t) === OID_CN) return texto(bytes, v)
    }
  }
  return null
}

function igual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

export interface CertificadoX509 {
  /** CN do sujeito (quem o certificado identifica). */
  sujeito: string | null
  /** CN de quem o emitiu. */
  emissor: string | null
  validoDe: string | null
  validoAte: string | null
}

/** Os campos que interessam de um certificado X.509 (DER). */
export function lerCertificadoNo(bytes: Uint8Array, cert: NoDer): CertificadoX509 {
  const tbs = cert.filhos[0]
  const vazio: CertificadoX509 = { sujeito: null, emissor: null, validoDe: null, validoAte: null }
  if (!tbs) return vazio
  const o = tbs.filhos[0]?.etiqueta === 0xa0 ? 1 : 0
  // tbsCertificate: [versão], série, algoritmo, emissor, validade, sujeito, ...
  const emissorNo = tbs.filhos[o + 2]
  const validade = tbs.filhos[o + 3]
  const sujeitoNo = tbs.filhos[o + 4]
  return {
    sujeito: sujeitoNo ? nomeComum(bytes, sujeitoNo) : null,
    emissor: emissorNo ? nomeComum(bytes, emissorNo) : null,
    validoDe: validade?.filhos.length === 2 ? data(bytes, validade.filhos[0]) : null,
    validoAte: validade?.filhos.length === 2 ? data(bytes, validade.filhos[1]) : null,
  }
}

export function lerCertificado(der: Uint8Array): CertificadoX509 {
  return lerCertificadoNo(der, lerNo(der, 0))
}

export interface AssinaturaCms {
  /** CN do certificado de quem assinou (o que o identifica o SignerInfo). */
  assinante: string | null
  /** CN de quem emitiu esse certificado. */
  emissor: string | null
  /** Validade do certificado do assinante. */
  certificadoValidoDe: string | null
  certificadoValidoAte: string | null
  /** Data declarada na assinatura (signingTime), se houver. */
  dataDeclarada: string | null
  /** Algoritmo de resumo do conteúdo assinado. */
  resumo: 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512' | null
  /** O resumo do conteúdo que ficou dentro da assinatura (messageDigest). */
  resumoAssinado: string | null
  /** Quantos certificados vêm dentro (assinante e cadeia). */
  certificados: number
}

/**
 * Lê uma assinatura CMS (SignedData), como a /Contents de um PDF
 * assinado em PAdES ou um `.p7s` de um contentor ASiC.
 *
 * Atira se não for um SignedData. Os bytes de enchimento a zero que os
 * PDF deixam no fim da /Contents ignoram-se (o DER diz o comprimento).
 */
export function lerAssinaturaCms(bytes: Uint8Array): AssinaturaCms {
  const raiz = lerNo(bytes, 0)
  const [tipo, embrulho] = raiz.filhos
  if (!tipo || tipo.etiqueta !== 0x06 || oid(bytes, tipo) !== OID_SIGNED_DATA || !embrulho) {
    throw new Error('não é uma assinatura CMS (SignedData)')
  }
  const sd = embrulho.filhos[0]
  if (!sd) throw new Error('SignedData vazio')
  // SignedData: version, digestAlgorithms, encapContentInfo,
  // [0] certificates (opcional), [1] crls (opcional), signerInfos.
  const certs = sd.filhos.find((f) => f.etiqueta === 0xa0)
  const signerInfos = sd.filhos[sd.filhos.length - 1]
  const signer = signerInfos?.filhos[0]
  if (!signer) throw new Error('assinatura sem SignerInfo')

  // SignerInfo: version, sid (issuerAndSerialNumber ou [0] SKI),
  // digestAlgorithm, [0] signedAttrs, ...
  const [, sid, algResumo] = signer.filhos
  const oidResumo = algResumo?.filhos[0] ? oid(bytes, algResumo.filhos[0]) : ''
  const resumo = RESUMOS[oidResumo] ?? null

  let dataDeclarada: string | null = null
  let resumoAssinado: string | null = null
  const atributos = signer.filhos.find((f) => f.etiqueta === 0xa0)
  for (const a of atributos?.filhos ?? []) {
    const [t, valores] = a.filhos
    if (!t || t.etiqueta !== 0x06) continue
    const id = oid(bytes, t)
    const v = valores?.filhos[0]
    if (!v) continue
    if (id === OID_MESSAGE_DIGEST && v.etiqueta === 0x04) resumoAssinado = hex(conteudo(bytes, v))
    if (id === OID_SIGNING_TIME) dataDeclarada = data(bytes, v)
  }

  // O certificado do assinante: o que tem o mesmo número de série do
  // issuerAndSerialNumber (ou, sem ele, o primeiro).
  const serie = sid && sid.etiqueta === 0x30 ? sid.filhos[1] : undefined
  let doAssinante: NoDer | undefined
  for (const c of certs?.filhos ?? []) {
    const tbs = c.filhos[0]
    if (!tbs) continue
    const temVersao = tbs.filhos[0]?.etiqueta === 0xa0
    const s = tbs.filhos[temVersao ? 1 : 0]
    if (serie && s && igual(conteudo(bytes, s), conteudo(bytes, serie))) {
      doAssinante = c
      break
    }
  }
  doAssinante ??= certs?.filhos[0]

  const cert = doAssinante
    ? lerCertificadoNo(bytes, doAssinante)
    : { sujeito: null, emissor: null, validoDe: null, validoAte: null }

  return {
    assinante: cert.sujeito,
    emissor: cert.emissor,
    certificadoValidoDe: cert.validoDe,
    certificadoValidoAte: cert.validoAte,
    dataDeclarada,
    resumo,
    resumoAssinado,
    certificados: certs?.filhos.length ?? 0,
  }
}
